import React, { useState, useEffect, useRef, useCallback } from 'react';
import { analytics } from '../services/googleAnalytics.ts';
import amplitudeAnalytics from '../services/amplitude.ts';

const AndroidDownloadBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const handleDismiss = useCallback(() => {
    try {
      analytics.trackEvent('android_modal_dismissed', {
        event_category: 'Android App',
        action: 'modal_dismissed'
      });
      amplitudeAnalytics.trackDownloadBannerDismissed('android');
    } catch (error) {
      console.error('Analytics error:', error);
    }

    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
    }, 300);
  }, []);

  const handleDownload = useCallback(() => {
    try {
      analytics.trackEvent('android_app_download_clicked', {
        event_category: 'Android App',
        action: 'download_clicked',
        event_label: 'APK Download'
      });
      amplitudeAnalytics.trackDownloadBannerClicked('android');
      amplitudeAnalytics.trackCriticalAction('Android App Download', {
        download_source: 'web_banner',
        file_type: 'apk'
      });
    } catch (error) {
      console.error('Analytics error:', error);
    }

    // Open the APK download link
    try {
      window.open('https://expo.dev/artifacts/eas/dQjPv1zZLnm5TzTZMj2ecT.apk', '_blank');
    } catch (error) {
      console.error('Window.open error:', error);
    }

    // Dismiss the modal after click
    handleDismiss();
  }, [handleDismiss]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    // Only close if clicking the backdrop, not the modal content
    if (e.target === e.currentTarget) {
      handleDismiss();
    }
  }, [handleDismiss]);

  useEffect(() => {
    // Check if user is on Android device
    const isAndroidDevice = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

    if (isAndroidDevice) {
      // Show banner after 2 seconds for Android users on every page refresh
      setTimeout(() => {
        setIsVisible(true);
        setIsAnimating(true);

        try {
          analytics.trackEvent('android_modal_shown', {
            event_category: 'Android App',
            action: 'modal_shown'
          });
          amplitudeAnalytics.trackDownloadBannerShown('android');
        } catch (error) {
          console.error('Analytics error:', error);
        }
      }, 2000);
    }
  }, []);

  // Handle escape key and focus management
  useEffect(() => {
    if (!isVisible) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleDismiss();
      }
    };

    // Focus the modal when it opens
    if (modalRef.current) {
      modalRef.current.focus();
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isVisible, handleDismiss]);

  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-[60] transition-all duration-300 ease-out ${
        isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Backdrop - handles clicks to close modal */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm cursor-pointer"
        onClick={handleBackdropClick}
      />

      {/* Modal container for centering */}
      <div className="relative h-full flex items-center justify-center p-4 pointer-events-none">
        {/* Modal */}
        <div
          ref={modalRef}
          tabIndex={-1}
          className={`relative bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all duration-300 ease-out pointer-events-auto ${
            isAnimating ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
          }`}
        >
        <div className="p-6">
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors"
            aria-label="Fermer"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Content */}
          <div className="text-center">
            {/* Android icon */}
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
              <span className="text-3xl animate-bounce" style={{animationDelay: '0.5s'}}>🤖</span>
            </div>

            <h2 className="text-xl font-bold mb-2">
              Application Android disponible !
            </h2>

            <p className="text-green-100 mb-6 text-sm leading-relaxed">
              Téléchargez l'application pour une expérience plus fluide avec les notifications push et une interface optimisée.
            </p>

            {/* Action buttons */}
            <div className="space-y-3">
              <button
                onClick={handleDownload}
                className="w-full bg-white text-green-600 px-6 py-3 rounded-xl font-semibold hover:bg-green-50 hover:scale-105 transition-all duration-200 shadow-lg flex items-center justify-center space-x-3 group"
              >
                <span className="text-xl animate-bounce">📲</span>
                <span>Télécharger l'APK</span>
                <div className="flex space-x-1">
                  <div className="w-1 h-1 bg-green-600 rounded-full animate-pulse"></div>
                  <div className="w-1 h-1 bg-green-600 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                  <div className="w-1 h-1 bg-green-600 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                </div>
              </button>

              <button
                onClick={handleDismiss}
                className="w-full text-green-100 px-6 py-2 rounded-xl font-medium hover:bg-white/10 transition-colors"
              >
                Peut-être plus tard
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default AndroidDownloadBanner;